import {Injectable, OnInit} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, delay, distinctUntilChanged, map, tap} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';

import {environment} from '@env/environment';
import {Claim} from '../../common/dialog-announcement/announcement.service';
import {AnnouncementUploadService} from '@app/views/admin/application-settings/announcement-upload.service';
import {DirectoryUser} from '@app/core/model/user';
import {State} from '@app/views/common/auth.reducers';
import {getApplication} from '@app/views/admin/application-settings/store/application-settings.reducers';
import {Application} from '@app/views/admin/application-settings/models/application.model';

@Injectable()
export class PrivacyPolicyService implements OnInit {

    public policyDocSaved: BehaviorSubject<PolicyDoc> = new BehaviorSubject(null);
    public isPolicyDocSaving: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private http: HttpClient,
        private uploadService: AnnouncementUploadService,
        private store: Store<State>
    ) {
        this.store.pipe(
            select(getApplication),
            distinctUntilChanged((prev: Application, curr: Application) => prev && curr && prev.guid === curr.guid)
        ).subscribe(() => {
            this.policyDocSaved.next(null);
        });
    }

    ngOnInit(): void {
    }

    getAllDocuments(): Observable<PolicyDoc[]> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/alldocs`;
        return this.http
            .get<PolicyDoc[]>(url)
            .pipe(catchError(this.handleError));
    }

    getDocument(id: number, withBody: boolean): Observable<PolicyDoc> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/doc/${id}?withBody=${withBody}`;
        return this.http
            .get<PolicyDoc>(url)
            .pipe(catchError(this.handleError));
    }

    getDocumentByName(name: string): Observable<PolicyDoc> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/docLastVersion/${name}`;
        return this.http
            .get<PolicyDoc>(url)
            .pipe(catchError(this.handleError));
    }

    getActualDocument(applicationGuid: string, districtGuid: string): Observable<DeliveryLog> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/last/app/claim/${districtGuid}/${applicationGuid}`;

        return this.http
            .get<DeliveryLog>(url)
            .pipe(
                map((deliveryLog: DeliveryLog) => {
                    deliveryLog.claim = new Claim().fromJSON(deliveryLog.claim);
                    return deliveryLog;
                }),
                catchError(this.handleError)
            );
    }

    getLastAcceptedDocument(applicationGuid: string, districtGuid: string): Observable<DeliveryLog> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/last/acceptance/app/claim/${districtGuid}/${applicationGuid}`;

        return this.http
            .get<DeliveryLog>(url)
            .pipe(
                map((deliveryLog: DeliveryLog) => {
                    deliveryLog.claim = new Claim().fromJSON(deliveryLog.claim);
                    return deliveryLog;
                }),
                catchError(this.handleError)
            );
    }

    getAllDocumentVersions(applicationGuid: string, claimName?: string): Observable<Claim[]> {
        const params = this.getQueryParams({
            claimName: claimName
        });

        const url = `${environment.datahubBaseURI}/api/v2/policies/app/claims/history/${applicationGuid}?${params}`;

        return this.http
            .get<Claim[]>(url)
            .pipe(catchError(this.handleError));
    }

    getPrivacyPolicyDeliveryHistory(applicationGuid: string, claimName: string, claimVersion?: string, type?: AnnouncementDeliveryType): Observable<DeliveryLogItem[]> {
        const params = this.getQueryParams({
            claimName: claimName,
            claimVersion: claimVersion,
            type: type
        });

        const url = `${environment.datahubBaseURI}/api/v2/policies/deliveries/history/for/${applicationGuid}?${params}`;

        return this.http
            .get<DeliveryLogItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    savePrivacyPolicy(privacyPolicy: SavePolicyModel, spot?: string, applicationGuid?: string): Observable<any> {
        this.isPolicyDocSaving.next(true);

        switch (privacyPolicy.savePolicyMethod) {
            case SavePolicyMethod.FILE_FROM_EDITOR:
                return this.saveDocFile(privacyPolicy.value, spot, applicationGuid).pipe(
                    tap(value => this.policyDocSaved.next(value)),
                    catchError(() => {
                        this.isPolicyDocSaving.next(false);
                        return throwError(new Error('Could not save Privacy Policy'));
                    }),
                );

            case SavePolicyMethod.EXTERNAL_URL:
                return this.uploadService.uploadUrl(privacyPolicy.value, applicationGuid).pipe(
                    tap(value => this.policyDocSaved.next(value)),
                    catchError(() => {
                        this.isPolicyDocSaving.next(false);
                        return throwError(new Error('Conversion of html file has failed'));
                    })
                );

            case SavePolicyMethod.UPLOADED_PDF:
                return this.uploadService.uploadPdf([privacyPolicy.value.rawFile], applicationGuid).pipe(
                    delay(1500),
                    tap(value => this.policyDocSaved.next(value)),
                    catchError(() => {
                        this.isPolicyDocSaving.next(false);
                        return throwError(new Error('Could not save Privacy Policy'));
                    })
                );
        }
    }

    saveDocFile(doc: PolicyDoc, spot?: string, application?: string): Observable<PolicyDoc> {
        let url = `${environment.datahubBaseURI}/api/v2/policies/save`;
        const p  = [];
        if (spot) {
            p.push('spot=' + spot);
        }
        if (application) {
            p.push('application=' + application);
        }
        if (p.length > 0) {
            url += '?' + p.join('&');
        }
        if (doc) {
            doc.version = this.updateVersion(doc.version || null);
        }
        return this.http
            .post<PolicyDoc>(url, doc)
            .pipe(catchError(this.handleError));
    }

    deletePrivacyPolicyDoc(doc: PolicyDoc) {
        let url = `${environment.datahubBaseURI}/api/v2/policies/delete/${doc.name}`;
        const p  = [];
        if (doc.version) {
            p.push('version=' + doc.version);
        }
        if (p.length > 0) {
            url += '?' + p.join('&');
        }
        return this.http
            .delete<PolicyDoc>(url)
            .pipe(catchError(this.handleError));
    }

    getAllConfirmations(orgGuid?: string): Observable<DeliveryLog[]> {
        const url = `${environment.datahubBaseURI}/api/v2/policies/allconfirmations?orgGuid=`;
        return this.http
            .get<DeliveryLog[]>(url)
            .pipe(catchError(this.handleError));
    }

    private updateVersion(version: string): string {
        return !version ? '1.0' : `1.${(Number.parseInt(version.slice(2)) || 0) + 1}`;
    }

    public getQueryParams(params: Object): string {
        return Object.keys(params)
            .filter((key: string) => params[key])
            .map((key: string) => `${key}=${params[key]}`)
            .join('&');
    }

    checkIsProviderApplicationClaim(claimName: string): boolean {
        return claimName.startsWith('provider.application');
    }

    checkIsApplicationClaim(claimName: string): boolean {
        return claimName.startsWith('provider.application') || claimName.startsWith('vendor.policy');
    }

    getSyncModeFromClaimName(claimName: string): string {
        const resultMatch = claimName.match(/(IMPORT|EXPORT|SYNC)/gm);
        return resultMatch ? resultMatch[0] : null;
    }

    getAppGuidFromClaimName(claimName: string): string {
        const resultMatch = claimName.match(new RegExp(`[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}`));
        return resultMatch ? resultMatch[0] : null;
    }

    getAppNameFromClaimTitle(claimTitle: string): string {
        return claimTitle.replace('Privacy Policy for ', '');
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}

export class PolicyDoc {
    id: number;
    name: string;
    title: string;
    version: string;
    regDate: Date;
    body: string;
    hasOrgAcceptance: boolean;
    hasDeliveries: boolean;
    userConfirmations: any[];
    rule: any;
    rules: any[];
    externalSource: string;
    internalFormat: string;
    status: number;
    ownerGuid: string;
}

export class DeliveryLog {
    claim: Claim;
    delivery: DeliveryModel;
    user: DirectoryUser;
}

export class DeliveryLogItem {
    deliveryModel: DeliveryModel;
    username: string;
    fullName: string;
    email: string;
    orgName?: string;
    providerApplicationName?: string;
    defaultApplicationName?: string;
}

export class DeliveryModel {
    claimName: string;
    claimVersion: string;
    userGuid: string;
    orgGuid: string;
    type: AnnouncementDeliveryType;
    eventDate: Date;
    body: string;
    attrValue: string;
    application: string;
    internalFormat: ClaimInternalFormat;
}

export enum AnnouncementDeliveryType {
    DELIVERY = 'DELIVERY',
    ACCEPTANCE = 'ACCEPTANCE',
    REJECT = 'REJECT'
}

export enum ClaimInternalFormat {
    HTML = 'html',
    PDF = 'pdf'
}

export class SavePolicyModel {
    value: any;
    savePolicyMethod: SavePolicyMethod;
}

export enum SavePolicyMethod {
    FILE_FROM_EDITOR = 'FILE_FROM_EDITOR',
    UPLOADED_PDF = 'UPLOADED_PDF',
    EXTERNAL_URL = 'EXTERNAL_URL'
}

export const GENERAL_CONNECT_DOCUMENTS = [
    'gg4l.connect.privacy.policy',
    'gg4l.connect.pp.eula',
    'gg4l.connect.pp.tou',
    'gg4l.connect.terms.use',
    'provider.application.IMPORT',
    'provider.application.EXPORT',
    'provider.application.SYNC',
];

export const SHOW_DOC_WITHOUT_TITLE = [
    'gg4l.connect.privacy.policy',
    'gg4l.connect.pp.eula',
    'gg4l.connect.pp.tou',
    'gg4l.connect.terms.use',
];
